import React, { Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface SelectProps {
    label?: string;
    value: string | number | null;
    onChange: (value: any) => void;
    options: SelectOption[];
    placeholder?: string;
    error?: string;
    helper?: string;
    disabled?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    className?: string;
}

const Select = ({
    label,
    value,
    onChange,
    options,
    placeholder = 'Select an option',
    error,
    helper,
    disabled = false,
    required = false,
    fullWidth = false,
    className,
}: SelectProps) => {
    const selectedOption = options.find((opt) => opt.value === value);
    const selectId = `select-${Math.random().toString(36).substr(2, 9)}`;

    return (
        <div className={cn('space-y-1.5', fullWidth ? 'w-full' : '', className)}>
            {label && (
                <label
                    htmlFor={selectId}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {label}
                    {required && <span className="text-error-500 ml-1">*</span>}
                </label>
            )}

            <Listbox value={value} onChange={onChange} disabled={disabled}>
                <div className="relative">
                    <Listbox.Button
                        id={selectId}
                        className={cn(
                            'relative w-full cursor-pointer rounded-lg',
                            'border px-4 py-2.5 text-left',
                            'text-sm text-gray-900 dark:text-gray-100',
                            'transition-all duration-200',
                            'focus:outline-none focus:ring-2 focus:ring-offset-0',
                            'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900',
                            error
                                ? 'border-error-500 bg-error-50 dark:bg-error-700 focus:border-error-500 focus:ring-error-500'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 focus:border-primary-500 focus:ring-primary-500'
                        )}
                    >
                        <span className={cn('block truncate', !selectedOption && 'text-gray-400 dark:text-gray-500')}>
                            {selectedOption ? selectedOption.label : placeholder}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </span>
                    </Listbox.Button>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Listbox.Options
                            className={cn(
                                'absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg',
                                'bg-white dark:bg-gray-800',
                                'py-1 shadow-lg ring-1 ring-black ring-opacity-5',
                                'focus:outline-none text-sm',
                                'custom-scrollbar'
                            )}
                        >
                            {options.map((option) => (
                                <Listbox.Option
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.disabled}
                                    className={({ active, disabled }) =>
                                        cn(
                                            'relative cursor-pointer select-none py-2.5 pl-10 pr-4',
                                            'transition-colors duration-150',
                                            active && !disabled
                                                ? 'bg-primary-50 dark:bg-gray-700 text-primary-900 dark:text-primary-100'
                                                : 'text-gray-900 dark:text-gray-100',
                                            disabled && 'opacity-50 cursor-not-allowed'
                                        )
                                    }
                                >
                                    {({ selected }) => (
                                        <>
                                            <span className={cn('block truncate', selected ? 'font-medium' : 'font-normal')}>
                                                {option.label}
                                            </span>
                                            {selected && (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600 dark:text-primary-400">
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                            )}
                                        </>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>

            {error && (
                <p id={`${selectId}-error`} className="text-sm text-error-600 dark:text-error-400">
                    {error}
                </p>
            )}

            {helper && !error && (
                <p id={`${selectId}-helper`} className="text-sm text-gray-500 dark:text-gray-400">
                    {helper}
                </p>
            )}
        </div>
    );
};

Select.displayName = 'Select';

export default Select;
